import { useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, Lock, Layers3, Save, RotateCcw, GripVertical, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

const COLORS = [
  { value: "teal", label: "Teal", chip: "bg-teal-600" },
  { value: "amber", label: "Amber", chip: "bg-amber-600" },
  { value: "pink", label: "Pink", chip: "bg-pink-600" },
  { value: "slate", label: "Slate", chip: "bg-slate-600" },
  { value: "emerald", label: "Emerald", chip: "bg-emerald-600" },
  { value: "rose", label: "Rose", chip: "bg-rose-600" },
  { value: "indigo", label: "Indigo", chip: "bg-indigo-600" },
  { value: "violet", label: "Violet", chip: "bg-violet-600" },
  { value: "orange", label: "Orange", chip: "bg-orange-600" },
  { value: "sky", label: "Sky", chip: "bg-sky-600" },
  { value: "gray", label: "Gray", chip: "bg-gray-600" },
];

export function colorChipClass(warna) {
  return (COLORS.find((c) => c.value === warna) || COLORS[3]).chip;
}

function empty() {
  return { nama: "", urutan: "100", warna: "slate", mode_kalkulasi: "absolute_sum", keterangan: "" };
}

const CALC_MODES = [
  {
    value: "netto_balance",
    label: "Netto Balance",
    desc: "Total = Σ Pendapatan − Σ Potongan. Cocok untuk grup THP.",
  },
  {
    value: "absolute_sum",
    label: "Absolute Sum",
    desc: "Total = Σ |nilai| — semua dijumlah positif. Cocok untuk Koperasi/Iuran.",
  },
];
const calcModeLabel = (v) => (CALC_MODES.find((m) => m.value === v) || CALC_MODES[1]).label;

export default function AdminReportGroups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/report-groups");
      setRows(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    setEditing(null);
    setForm(empty());
    setOpen(true);
  }

  function openEdit(r) {
    setEditing(r);
    setForm({
      nama: r.nama || "",
      urutan: String(r.urutan ?? 100),
      warna: r.warna || "slate",
      mode_kalkulasi: r.mode_kalkulasi || "absolute_sum",
      keterangan: r.keterangan || "",
    });
    setOpen(true);
  }

  async function submit() {
    if (form.nama.trim().length < 2) {
      toast.error("Nama grup minimal 2 karakter");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nama: form.nama.trim(),
        urutan: parseInt(form.urutan || "100", 10) || 100,
        warna: form.warna || "slate",
        mode_kalkulasi: form.mode_kalkulasi || "absolute_sum",
        keterangan: form.keterangan.trim() || null,
      };
      if (editing) {
        await api.put(`/report-groups/${editing.id}`, payload);
        toast.success("Grup Laporan diperbarui");
      } else {
        await api.post("/report-groups", payload);
        toast.success("Grup Laporan ditambahkan");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove(r) {
    if (r.is_system) {
      toast.error("Grup sistem tidak dapat dihapus");
      return;
    }
    if (!window.confirm(`Hapus grup laporan "${r.nama}"? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/report-groups/${r.id}`);
      toast.success("Grup Laporan dihapus");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-report-groups">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-teal-700 font-bold">Administrasi</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <Layers3 size={28} className="text-teal-700" /> Master Grup Laporan
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Kelola kategori pengelompokan komponen di <b>Rekap Pembebanan</b>. Setiap komponen di
            Master Komponen dipetakan ke salah satu grup di sini.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="gap-2" data-testid="reload-groups">
            <RotateCcw size={14} /> Muat Ulang
          </Button>
          <Button onClick={openCreate} className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="add-group-btn">
            <Plus size={14} /> Tambah Grup
          </Button>
        </div>
      </header>

      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 flex items-start gap-2">
        <Palette size={14} className="text-slate-500 mt-0.5" />
        <div className="text-[11px] text-slate-600 leading-relaxed">
          <b>Cara pakai:</b> tambahkan grup baru (mis. "Bonus Kinerja", "Cicilan Motor") dengan
          urutan yang menentukan posisi di Excel Rekap Pembebanan. Grup Laporan <b>murni untuk
          laporan</b> — tidak memengaruhi perhitungan payroll. Grup default (THP, Lembur,
          Potongan Koperasi, Fasilitas/Eksklusi, Lainnya) ditandai <b>Sistem</b> — nama, urutan,
          warna, dan keterangan bebas diubah, tetapi tidak dapat dihapus.
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Urut", "Warna", "Nama Grup", "Mode Kalkulasi", "Keterangan", "Status", ""].map((h, i) => (
                <TableHead key={i} className={`uppercase text-[10px] tracking-widest font-bold text-slate-500 ${i === 6 ? "text-right" : ""}`}>
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-slate-500">Memuat…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-400">
                  Belum ada grup. Klik "Tambah Grup" untuk membuat.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`group-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <TableCell className="font-mono-clock text-xs text-slate-500">{r.urutan}</TableCell>
                  <TableCell>
                    <span className={`inline-block w-4 h-4 rounded ${colorChipClass(r.warna)} border border-white shadow-sm`} title={r.warna} />
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                      {r.nama}
                      {r.is_system && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal-700 text-white">
                          <Lock size={9} /> Sistem
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded border whitespace-nowrap ${
                        (r.mode_kalkulasi || "absolute_sum") === "netto_balance"
                          ? "bg-teal-50 text-teal-800 border-teal-200"
                          : "bg-violet-50 text-violet-800 border-violet-200"
                      }`}
                      data-testid={`group-mode-${r.id}`}
                    >
                      {calcModeLabel(r.mode_kalkulasi)}
                    </span>
                  </TableCell>
                  <TableCell className="text-[11px] text-slate-500 max-w-[260px] truncate" title={r.keterangan || ""}>
                    {r.keterangan || "—"}
                  </TableCell>
                  <TableCell>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                      Aktif
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => openEdit(r)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900 px-2 py-1 rounded hover:bg-teal-50"
                        data-testid={`edit-group-${r.id}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      {!r.is_system && (
                        <button
                          onClick={() => remove(r)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-900 px-2 py-1 rounded hover:bg-rose-50"
                          data-testid={`delete-group-${r.id}`}
                        >
                          <Trash2 size={12} /> Hapus
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-800">
              <Layers3 size={18} /> {editing ? "Edit Grup Laporan" : "Tambah Grup Laporan"}
            </DialogTitle>
            <DialogDescription>
              Grup ini akan muncul sebagai kategori pengelompokan di Excel Rekap Pembebanan.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {editing?.is_system && (
              <div className="sm:col-span-2 rounded-lg border border-teal-200 bg-teal-50/60 p-3 flex items-start gap-2">
                <Lock size={14} className="text-teal-700 mt-0.5" />
                <div className="text-[11px] text-teal-900 leading-relaxed">
                  <b>Grup Sistem.</b> Grup ini tidak dapat dihapus (menjadi acuan fallback),
                  namun nama, urutan, warna, dan keterangan bebas Anda ubah.
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama Grup</Label>
              <Input
                className="mt-1.5"
                placeholder="Contoh: Bonus Kinerja"
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                data-testid="input-group-nama"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Urutan (Excel)</Label>
              <Input
                className="mt-1.5 font-mono-clock"
                inputMode="numeric"
                placeholder="100"
                value={form.urutan}
                onChange={(e) => setForm({ ...form, urutan: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                data-testid="input-group-urutan"
              />
              <p className="text-[11px] text-slate-400 mt-1">Angka kecil muncul lebih dulu.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Warna Header Excel</Label>
              <Select value={form.warna} onValueChange={(v) => setForm({ ...form, warna: v })}>
                <SelectTrigger className="mt-1.5" data-testid="input-group-warna">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-3.5 h-3.5 rounded ${c.chip}`} />
                        <span>{c.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Mode Kalkulasi</Label>
              <Select
                value={form.mode_kalkulasi}
                onValueChange={(v) => setForm({ ...form, mode_kalkulasi: v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="input-group-mode-kalkulasi">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CALC_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value} data-testid={`mode-opt-${m.value}`}>
                      <div className="flex flex-col py-0.5">
                        <span className="font-semibold">{m.label}</span>
                        <span className="text-[11px] text-slate-500">{m.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">
                Menentukan cara sub-total grup dihitung di Excel Rekap Pembebanan.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Keterangan (opsional)</Label>
              <Textarea
                className="mt-1.5"
                placeholder="Deskripsi singkat kategori (muncul di banner Excel)"
                rows={2}
                value={form.keterangan}
                onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                data-testid="input-group-keterangan"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="group-cancel">
              Batal
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800 gap-2"
              data-testid="group-submit"
            >
              <Save size={14} /> {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
